import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CheckCircleOutlined, CloseCircleOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  message
} from "antd";
import dayjs from "dayjs";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import { getWorkplaceNumber } from "@core/runtime-settings";
import { fetchSprPredList } from "../sprs/pred/spr-pred-api";
import {
  createWarehouseDocument,
  fetchWarehouseDocument,
  fetchWarehouseProducts,
  fetchWarehouseRestCheck,
  postWarehouseDocument,
  unpostWarehouseDocument,
  updateWarehouseDocument
} from "./warehouse-api";
import WarehouseShortageList from "./warehouse-shortage-list";
import { formatNumber, normalizeDocument, normalizeRestCheck, pick, toNumber } from "./warehouse-utils";

const IN_STOCK_ONLY_KEY = "warehouse.document.inStockOnly";

const readInStockOnly = () => {
  try {
    return window.localStorage.getItem(IN_STOCK_ONLY_KEY) === "1";
  } catch {
    return false;
  }
};

const writeInStockOnly = (value) => {
  try {
    window.localStorage.setItem(IN_STOCK_ONLY_KEY, value ? "1" : "0");
  } catch {
    // localStorage недоступен — флаг живёт только в текущей сессии
  }
};

/** Склад, по которому показываем остаток: отправитель для расхода и перемещения. */
const senderWarehouseVids = [-1, 3];

const productOption = ({ value, name, funit, rest }) => ({
  value,
  name,
  funit,
  rest,
  label:
    rest == null ? (
      name
    ) : (
      <span className="warehouse-document-drawer__product-option">
        <span>{name}</span>
        <span className={`warehouse-document-drawer__product-rest${rest > 0 ? "" : " warehouse-document-drawer__product-rest--empty"}`}>
          {formatNumber(rest)} {funit}
        </span>
      </span>
    )
});

const productOptionRows = (data) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) =>
      productOption({
        value: toNumber(pick(row, "idTov", "IdTov")),
        name: String(pick(row, "name", "Name") ?? ""),
        funit: String(pick(row, "funit", "Funit") ?? ""),
        rest: toNumber(pick(row, "rest", "Rest"))
      })
    )
    .filter((row) => row.value != null);
};

const optionRows = (data, idKeys, labelKeys) => {
  const rows = Array.isArray(data) ? data : pick(data, "items", "Items") || [];
  return rows
    .map((row) => ({
      value: toNumber(pick(row, ...idKeys)),
      label: String(pick(row, ...labelKeys) ?? ""),
      funit: String(pick(row, "funit", "Funit") ?? "")
    }))
    .filter((row) => row.value != null);
};

const mergeOptions = (previous, next) => {
  const map = new Map([...previous, ...next].map((item) => [item.value, item]));
  return [...map.values()];
};

const withSelected = (options, record) => {
  if (!record.idTov || options.some((item) => item.value === record.idTov)) return options;
  return [
    productOption({ value: record.idTov, name: record.name || `#${record.idTov}`, funit: record.funit, rest: null }),
    ...options
  ];
};

const formatSum = (value) =>
  Number(value || 0).toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const WarehouseDocumentDrawer = ({ open, id, lookups, onClose, onSaved, onChanged }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [posted, setPosted] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [lines, setLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [counterpartyLoading, setCounterpartyLoading] = useState(false);
  const [focusKey, setFocusKey] = useState(null);
  const [inStockOnly, setInStockOnly] = useState(readInStockOnly);
  const requestRef = useRef(0);
  const productRequestRef = useRef(0);
  const productTimerRef = useRef();
  const counterpartyTimerRef = useRef();
  const counterpartyRequestRef = useRef(0);
  const isNew = id == null;
  const vid = Form.useWatch("vid", form);
  const idFrom = Form.useWatch("idFrom", form);
  const isInventory = Number(vid) === 4;
  const busy = loading || saving || posting;
  const disabled = busy || posted;
  const senderWarehouseId = senderWarehouseVids.includes(Number(vid)) ? toNumber(idFrom) : undefined;

  const loadProducts = useCallback(
    (query = "", stockOnly = inStockOnly) => {
      clearTimeout(productTimerRef.current);
      productTimerRef.current = setTimeout(() => {
        const requestId = ++productRequestRef.current;
        setProductLoading(true);
        const docdate = form.getFieldValue("docdate");
        fetchWarehouseProducts({
          query,
          idSkl: senderWarehouseId,
          date: senderWarehouseId && docdate ? dayjs(docdate).format("YYYY-MM-DD") : undefined,
          inStockOnly: senderWarehouseId ? stockOnly : undefined
        })
          .then((data) => {
            if (requestId !== productRequestRef.current) return;
            setProducts(productOptionRows(data));
          })
          .catch(() => undefined)
          .finally(() => {
            if (requestId === productRequestRef.current) setProductLoading(false);
          });
      }, 300);
    },
    [form, inStockOnly, senderWarehouseId]
  );

  const loadCounterparties = useCallback((query = "") => {
    clearTimeout(counterpartyTimerRef.current);
    counterpartyTimerRef.current = setTimeout(() => {
      const requestId = ++counterpartyRequestRef.current;
      setCounterpartyLoading(true);
      fetchSprPredList({ q: query, take: 50 })
        .then((data) => {
          if (requestId !== counterpartyRequestRef.current) return;
          const next = optionRows(data, ["idPred", "IdPred"], ["name", "Name"]);
          const { idFrom, idDest } = form.getFieldsValue(["idFrom", "idDest"]);
          setCounterparties((previous) =>
            mergeOptions(
              previous.filter(
                (item) =>
                  (item.value === idFrom || item.value === idDest) &&
                  !next.some((option) => option.value === item.value)
              ),
              next
            )
          );
        })
        .catch(() => undefined)
        .finally(() => {
          if (requestId === counterpartyRequestRef.current) setCounterpartyLoading(false);
        });
    }, 300);
  }, [form]);

  useEffect(() => {
    if (!open) return undefined;
    const requestId = ++requestRef.current;
    setLoading(true);
    setPosted(false);
    setDirty(false);
    setProducts([]);
    setCounterparties([]);
    (isNew ? Promise.resolve(null) : fetchWarehouseDocument(id))
      .then((data) => {
        if (requestId !== requestRef.current) return;
        if (!data) {
          const warehouseId = getWorkplaceNumber("idSkl", 0) || undefined;
          form.resetFields();
          form.setFieldsValue({
            docdate: dayjs(),
            vid: 1,
            idDest: warehouseId,
            sfDocnum: "",
            sfDocdate: null
          });
          setLines([]);
          return;
        }
        const document = normalizeDocument(data);
        setPosted(document.isAct);
        form.setFieldsValue({
          docdate: document.docdate ? dayjs(document.docdate) : null,
          vid: document.vid,
          idFrom: document.idFrom,
          idDest: document.idDest,
          komment: document.komment,
          sfDocnum: document.sfDocnum,
          sfDocdate: document.sfDocdate ? dayjs(document.sfDocdate) : null
        });
        setLines(document.lines);
        const counterparty =
          document.vid === 1
            ? { value: document.idFrom, label: document.fromName }
            : document.vid === -1
              ? { value: document.idDest, label: document.destName }
              : null;
        if (counterparty?.value) setCounterparties([counterparty]);
        setProducts(
          document.lines
            .filter((line) => line.idTov)
            .map((line) =>
              productOption({ value: line.idTov, name: line.name || `#${line.idTov}`, funit: line.funit, rest: null })
            )
        );
      })
      .catch((error) => message.error(error.message || "Не удалось загрузить накладную"))
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
    loadCounterparties("");
    return () => {
      requestRef.current += 1;
      clearTimeout(productTimerRef.current);
      clearTimeout(counterpartyTimerRef.current);
      productRequestRef.current += 1;
      counterpartyRequestRef.current += 1;
    };
  }, [form, id, isNew, loadCounterparties, open, reloadToken]);

  const applyVidDefaults = (nextVid) => {
    const warehouseId = getWorkplaceNumber("idSkl", 0) || undefined;
    if (nextVid === 1) form.setFieldsValue({ idFrom: undefined, idDest: warehouseId });
    if (nextVid === -1) form.setFieldsValue({ idFrom: warehouseId, idDest: undefined });
    if (nextVid === 3) form.setFieldsValue({ idFrom: warehouseId, idDest: undefined });
    if (nextVid === 4) form.setFieldsValue({ idFrom: warehouseId, idDest: warehouseId });
  };

  const endpointField = (name, label, counterparty, wide = false) => (
    <Form.Item
      name={name}
      label={label}
      className={wide ? "warehouse-document-drawer__span-2" : undefined}
      rules={[{ required: true, message: `Укажите: ${label.toLowerCase()}` }]}
    >
      <Select
        placeholder="Выберите"
        showSearch
        optionFilterProp={counterparty ? undefined : "label"}
        filterOption={counterparty ? false : undefined}
        options={counterparty ? counterparties : lookups.warehouses}
        loading={counterparty ? counterpartyLoading : false}
        onSearch={counterparty ? loadCounterparties : undefined}
        onDropdownVisibleChange={
          counterparty ? (visible) => visible && loadCounterparties("") : undefined
        }
      />
    </Form.Item>
  );

  const updateLine = (key, values) => {
    setDirty(true);
    setLines((previous) =>
      previous.map((line) => {
        if (line.key !== key) return line;
        const next = { ...line, ...values };
        return {
          ...next,
          syma: Number(next.kol || 0) * Number(next.zena || 0) * Number(next.koeff || 1),
          kolDiff: Number(next.kolFact || 0) - Number(next.kol || 0)
        };
      })
    );
  };

  const removeLine = (key) => {
    setDirty(true);
    setLines((previous) => previous.filter((line) => line.key !== key));
  };

  const columns = useMemo(() => {
    const result = [
      {
        title: "Товар",
        dataIndex: "idTov",
        key: "idTov",
        render: (value, record) => (
          <Select
            size="small"
            value={value}
            placeholder="Выберите товар"
            autoFocus={record.key === focusKey}
            defaultOpen={record.key === focusKey}
            showSearch
            filterOption={false}
            optionLabelProp="name"
            loading={productLoading}
            options={withSelected(products, record)}
            onSearch={(query) => loadProducts(query)}
            onDropdownVisibleChange={(visible) => visible && loadProducts("")}
            onChange={(next, option) =>
              updateLine(record.key, { idTov: next, name: option?.name || "", funit: option?.funit || "" })
            }
            popupRender={(menu) =>
              senderWarehouseId ? (
                <>
                  <div className="warehouse-document-drawer__product-filter">
                    <Checkbox
                      checked={inStockOnly}
                      onMouseDown={(event) => event.preventDefault()}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setInStockOnly(checked);
                        writeInStockOnly(checked);
                        loadProducts("", checked);
                      }}
                    >
                      Только в наличии
                    </Checkbox>
                  </div>
                  {menu}
                </>
              ) : (
                menu
              )
            }
            disabled={disabled}
            style={{ width: "100%" }}
          />
        )
      },
      { title: "Ед.", dataIndex: "funit", key: "funit", width: 56 },
      {
        title: "Кол.",
        dataIndex: "kol",
        key: "kol",
        width: 96,
        align: "right",
        render: (value, record) => (
          <InputNumber
            size="small"
            controls={false}
            value={value}
            min={0}
            step={0.001}
            disabled={disabled}
            onChange={(next) => updateLine(record.key, { kol: next ?? 0 })}
            style={{ width: "100%" }}
          />
        )
      }
    ];
    if (isInventory) {
      result.push({
        title: "Факт",
        dataIndex: "kolFact",
        key: "kolFact",
        width: 96,
        align: "right",
        render: (value, record) => (
          <InputNumber
            size="small"
            controls={false}
            value={value}
            min={0}
            step={0.001}
            disabled={disabled}
            onChange={(next) => updateLine(record.key, { kolFact: next ?? 0 })}
            style={{ width: "100%" }}
          />
        )
      });
      result.push({ title: "Разница", dataIndex: "kolDiff", key: "kolDiff", width: 84, align: "right" });
    }
    result.push(
      {
        title: "Цена",
        dataIndex: "zena",
        key: "zena",
        width: 104,
        align: "right",
        render: (value, record) => (
          <InputNumber
            size="small"
            controls={false}
            value={value}
            min={0}
            step={0.01}
            disabled={disabled}
            onChange={(next) => updateLine(record.key, { zena: next ?? 0 })}
            style={{ width: "100%" }}
          />
        )
      },
      {
        title: "Коэф.",
        dataIndex: "koeff",
        key: "koeff",
        width: 76,
        align: "right",
        render: (value, record) => (
          <InputNumber
            size="small"
            controls={false}
            value={value}
            min={0}
            step={0.001}
            disabled={disabled}
            onChange={(next) => updateLine(record.key, { koeff: next ?? 1 })}
            style={{ width: "100%" }}
          />
        )
      },
      {
        title: "Сумма",
        dataIndex: "syma",
        key: "syma",
        width: 110,
        align: "right",
        render: formatSum
      },
      {
        key: "actions",
        width: 36,
        render: (_, record) => (
          <Tooltip title="Удалить строку">
            <Button
              type="text"
              size="small"
              danger
              disabled={disabled}
              icon={<DeleteOutlined />}
              onClick={() => removeLine(record.key)}
            />
          </Tooltip>
        )
      }
    );
    return result;
  }, [disabled, focusKey, inStockOnly, isInventory, loadProducts, productLoading, products, senderWarehouseId]);

  const totals = useMemo(
    () =>
      lines.reduce(
        (acc, line) => ({
          kol: acc.kol + Number(line.kol || 0),
          syma: acc.syma + Number(line.syma || 0)
        }),
        { kol: 0, syma: 0 }
      ),
    [lines]
  );

  const addLine = () => {
    const key = `new-${Date.now()}`;
    setDirty(true);
    setFocusKey(key);
    loadProducts("");
    setLines((previous) => [
      ...previous,
      {
        key,
        idStr: null,
        idTov: null,
        name: "",
        funit: "",
        kol: 1,
        kolFact: 0,
        zena: 0,
        koeff: 1,
        syma: 0,
        kolDiff: -1
      }
    ]);
  };

  /** Сохраняет документ; возвращает сохранённую накладную или null, если валидация не прошла / ошибка. */
  const persist = async () => {
    try {
      const values = await form.validateFields();
      if (lines.some((line) => !line.idTov)) {
        message.warning("Укажите товар во всех строках");
        return null;
      }
      setSaving(true);
      const body = {
        docdate: values.docdate?.format("YYYY-MM-DD") || null,
        idFrom: values.idFrom,
        idDest: values.vid === 4 ? values.idFrom : values.idDest,
        komment: values.komment || "",
        vid: values.vid,
        sfDocnum: values.sfDocnum || "",
        sfDocdate: values.sfDocdate?.format("YYYY-MM-DD") || null,
        lines: lines.map((line) => ({
          ...(line.idStr != null ? { idStr: line.idStr } : {}),
          idTov: line.idTov,
          kol: Number(line.kol || 0),
          kolFact: isInventory ? Number(line.kolFact || 0) : null,
          zena: Number(line.zena || 0),
          koeff: Number(line.koeff || 1)
        }))
      };
      const saved = normalizeDocument(
        isNew ? await createWarehouseDocument(body) : await updateWarehouseDocument(id, body)
      );
      setDirty(false);
      return saved;
    } catch (error) {
      if (!error?.errorFields) message.error(error.message || "Не удалось сохранить накладную");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const saved = await persist();
    if (!saved) return;
    message.success(isNew ? "Накладная создана" : "Накладная сохранена");
    onSaved?.(saved);
  };

  const runPost = async (docId) => {
    setPosting(true);
    try {
      const saved = normalizeDocument(await postWarehouseDocument(docId));
      message.success("Накладная проведена");
      setReloadToken((token) => token + 1);
      onChanged?.(saved);
    } catch (error) {
      message.error(error.message || "Не удалось провести накладную");
    } finally {
      setPosting(false);
    }
  };

  /** Провести: при изменениях (или новой накладной) сначала сохраняет, затем проверяет остатки. */
  const post = async () => {
    let docId = id;
    if (isNew || dirty) {
      const saved = await persist();
      if (!saved) return;
      docId = saved.idDoc;
      onChanged?.(saved);
    }
    let check;
    try {
      setPosting(true);
      check = normalizeRestCheck(await fetchWarehouseRestCheck(docId));
    } catch (error) {
      message.error(error.message || "Не удалось проверить остатки");
      return;
    } finally {
      setPosting(false);
    }
    if (!check.items.length) {
      await runPost(docId);
    } else if (check.mode === 2) {
      Modal.error({
        title: "Проведение запрещено",
        content: <WarehouseShortageList items={check.items} />,
        okText: "Закрыть"
      });
    } else {
      Modal.confirm({
        title: "Остаток уйдёт в минус",
        content: <WarehouseShortageList items={check.items} />,
        okText: "Провести всё равно",
        okButtonProps: { danger: true },
        cancelText: "Отмена",
        onOk: () => runPost(docId)
      });
    }
  };

  const unpost = async () => {
    setPosting(true);
    try {
      const saved = normalizeDocument(await unpostWarehouseDocument(id));
      message.success("Проведение отменено");
      setReloadToken((token) => token + 1);
      onChanged?.(saved);
    } catch (error) {
      message.error(error.message || "Не удалось отменить проведение");
    } finally {
      setPosting(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      destroyOnClose
      width="min(960px, calc(100vw - 80px))"
      title={
        <span className="warehouse-document-drawer__title">
          {isNew ? "Новая накладная" : `Накладная #${id}`}
          {posted ? (
            <Tooltip title="Проведённый документ доступен только для просмотра">
              <Tag color="green">Проведена</Tag>
            </Tooltip>
          ) : null}
        </span>
      }
      rootClassName="warehouse-document-drawer"
      extra={<ReportButton vid={REPORT_VID.WAREHOUSE_DOCUMENTS} id={id} compact disabled={isNew} />}
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Button
              type="primary"
              loading={saving}
              disabled={busy || posted}
              onClick={save}
              className="warehouse-document-drawer__ok"
            >
              ОК
            </Button>
            {posted ? (
              <Popconfirm
                title="Отменить проведение?"
                okText="Отменить проведение"
                cancelText="Отмена"
                onConfirm={unpost}
                disabled={busy}
              >
                <Button icon={<CloseCircleOutlined />} loading={posting} disabled={busy}>
                  Снять проведение
                </Button>
              </Popconfirm>
            ) : (
              <Button icon={<CheckCircleOutlined />} loading={posting} disabled={busy} onClick={post}>
                Провести
              </Button>
            )}
          </Space>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          disabled={disabled}
          className="warehouse-document-drawer__form"
          onValuesChange={() => setDirty(true)}
        >
          <div className="warehouse-document-drawer__header">
            <Form.Item name="docdate" label="Дата" rules={[{ required: true, message: "Укажите дату" }]}>
              <DatePicker format="DD.MM.YYYY" suffixIcon={null} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="vid" label="Вид" rules={[{ required: true, message: "Укажите вид" }]}>
              <Select options={lookups.documentTypes} onChange={applyVidDefaults} />
            </Form.Item>
            {vid === 1 && endpointField("idFrom", "Поставщик", true)}
            {vid === 1 && endpointField("idDest", "Склад-получатель", false)}
            {vid === -1 && endpointField("idFrom", "Склад-отправитель", false)}
            {vid === -1 && endpointField("idDest", "Получатель", true)}
            {vid === 3 && endpointField("idFrom", "Склад-отправитель", false)}
            {vid === 3 && endpointField("idDest", "Склад-получатель", false)}
            {vid === 4 && endpointField("idFrom", "Склад", false, true)}
            <Form.Item name="sfDocnum" label="№ счёта-фактуры">
              <Input />
            </Form.Item>
            <Form.Item name="sfDocdate" label="Дата счёта-фактуры">
              <DatePicker format="DD.MM.YYYY" suffixIcon={null} placeholder="" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="komment" label="Комментарий" className="warehouse-document-drawer__span-2">
              <Input />
            </Form.Item>
          </div>
          <Table
            className="warehouse-document-drawer__lines"
            size="small"
            rowKey="key"
            columns={columns}
            dataSource={lines}
            pagination={false}
            scroll={{ x: 720 }}
            locale={{ emptyText: "Нет строк — добавьте товар" }}
            summary={() =>
              lines.length ? (
                <Table.Summary.Row className="warehouse-document-drawer__total">
                  <Table.Summary.Cell index={0} colSpan={2}>
                    Итого · {lines.length} поз.
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    {totals.kol.toLocaleString("ru-RU", { maximumFractionDigits: 3 })}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} colSpan={isInventory ? 4 : 2} />
                  <Table.Summary.Cell index={isInventory ? 7 : 5} align="right">
                    {formatSum(totals.syma)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={isInventory ? 8 : 6} />
                </Table.Summary.Row>
              ) : null
            }
          />
          {!posted ? (
            <Button
              type="dashed"
              block
              size="small"
              icon={<PlusOutlined />}
              disabled={disabled}
              onClick={addLine}
              className="warehouse-document-drawer__add"
            >
              Добавить строку
            </Button>
          ) : null}
        </Form>
      </Spin>
    </Drawer>
  );
};

export default WarehouseDocumentDrawer;
